/**
 * Built-in stock reports available to all governments.
 */
export const stockReports = [
    {
        slug: 'board-health-overview',
        name: 'Board Health Overview',
        description: 'Governance snapshot showing vacancies, term expirations, and applicant pipeline across all boards.',
        outputType: 'table',
        query: {
            measures: ['board_health.count'],
            dimensions: [
                'meeting_bodies.name',
                'meeting_bodies.type',
                'board_health.title',
                'board_health.is_vacant',
                'board_health.current_term_status',
                'board_health.days_until_expiration',
                'board_health.current_member_first_name',
                'board_health.current_member_last_name',
                'board_health.active_applications',
            ],
            groupBy: ['meeting_bodies.name'],
            order: { 'meeting_bodies.name': 'asc' },
        },
    },
    {
        slug: 'current-member-roster',
        name: 'Current Member Roster',
        description: 'All currently seated members with their positions, terms, and contact information.',
        outputType: 'table',
        query: {
            measures: ['member_roster.count'],
            dimensions: [
                'meeting_bodies.name',
                'meeting_members.first_name',
                'meeting_members.last_name',
                'meeting_members.email',
                'meeting_body_positions.title',
                'meeting_body_seat_terms.status',
                'meeting_body_seat_terms.days_remaining',
            ],
            filters: [
                {
                    member: 'meeting_body_seat_terms.is_current',
                    operator: 'equals',
                    values: ['true'],
                },
            ],
            groupBy: ['meeting_bodies.name'],
            order: { 'meeting_bodies.name': 'asc' },
        },
    },
    {
        slug: 'meeting-body-directory',
        name: 'Meeting Body Directory',
        description: 'Complete directory of all active meeting bodies with contact and meeting details.',
        outputType: 'table',
        query: {
            measures: ['meeting_bodies.count'],
            dimensions: [
                'meeting_bodies.name',
                'meeting_bodies.type',
                'meeting_bodies.contact_name',
                'meeting_bodies.contact_email',
                'meeting_bodies.meeting_frequency',
                'meeting_bodies.location',
            ],
            filters: [
                {
                    member: 'meeting_bodies.is_active',
                    operator: 'equals',
                    values: ['true'],
                },
            ],
            groupBy: ['meeting_bodies.name'],
            order: { 'meeting_bodies.name': 'asc' },
        },
    },
    {
        slug: 'expiring-terms',
        name: 'Expiring Terms',
        description: 'Members whose terms are ending within 90 days, helping with succession planning.',
        outputType: 'table',
        query: {
            measures: ['member_roster.count'],
            dimensions: [
                'meeting_bodies.name',
                'meeting_members.first_name',
                'meeting_members.last_name',
                'meeting_body_positions.title',
                'meeting_body_seat_terms.days_remaining',
                'meeting_body_seat_terms.number',
            ],
            filters: [
                {
                    member: 'meeting_body_seat_terms.is_current',
                    operator: 'equals',
                    values: ['true'],
                },
                {
                    member: 'meeting_body_seat_terms.days_remaining',
                    operator: 'lte',
                    values: ['90'],
                },
                {
                    member: 'meeting_body_seat_terms.days_remaining',
                    operator: 'gte',
                    values: ['0'],
                },
            ],
            groupBy: ['meeting_bodies.name'],
            order: { 'meeting_bodies.name': 'asc' },
        },
    },
    {
        slug: 'vacant-seats',
        name: 'Vacant Seats',
        description: 'All currently vacant seats with application status and requirements.',
        outputType: 'table',
        query: {
            measures: ['board_health.count'],
            dimensions: [
                'meeting_bodies.name',
                'board_health.title',
                'board_health.type',
                'board_health.appointed_by',
                'board_health.allow_applications',
                'board_health.active_applications',
                'board_health.total_applications',
            ],
            filters: [
                {
                    member: 'board_health.is_vacant',
                    operator: 'equals',
                    values: ['true'],
                },
            ],
            groupBy: ['meeting_bodies.name'],
            order: { 'meeting_bodies.name': 'asc' },
        },
    },
    {
        slug: 'vote-records',
        name: 'Vote Records',
        description: 'Every recorded vote by member, with the mover, seconder, and outcome of each motion.',
        outputType: 'table',
        query: {
            measures: ['vote_records.count'],
            dimensions: [
                'meetings.title',
                'legislative_items.code',
                'legislative_items.full_title',
                'minutes_motions.action_name',
                'minutes_motions.mover_name',
                'minutes_motions.seconder_name',
                'meeting_members.first_name',
                'meeting_members.last_name',
                'vote_records.vote',
                'minutes_motions.outcome',
            ],
            timeDimensions: [{ dimension: 'meetings.date', granularity: 'day' }],
            groupBy: ['meetings.title'],
            order: { 'meetings.title': 'asc' },
        },
    },
    {
        slug: 'attendance-records',
        name: 'Attendance Records',
        description: 'Member attendance at every roll call, including excused absences and in-person or remote participation.',
        outputType: 'table',
        query: {
            measures: ['attendance_records.count'],
            dimensions: [
                'meetings.title',
                'meeting_bodies.name',
                'meeting_members.first_name',
                'meeting_members.last_name',
                'attendance_records.status',
                'attendance_records.attendance_mode',
                'attendance_records.notes',
            ],
            timeDimensions: [{ dimension: 'meetings.date', granularity: 'day' }],
            groupBy: ['meetings.title'],
            order: { 'meetings.title': 'asc' },
        },
    },
];
/**
 * Looks up a stock report definition by its slug.
 */
export const getStockReportBySlug = (slug) => stockReports.find((report) => report.slug === slug);
